import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { AccountContext } from 'context/account';
import { fontWeightMap } from 'helpers/constants';
import { usePathname } from 'i18n/routing';
import { useCart } from 'frontastic';

/**
 * Custom hook to fetch the user's name based on the default address or recent orders.
 * It first checks for a default address and returns the name from there. If no default address is found,
 * it looks into the recent orders for a billing address to extract the name. If neither is available, it returns an empty string.
 *
 * @returns {string} The user's name in the format "FirstName LastName" or an empty string if not found.
 */
export const useUserName = (): string => {
  const { account, accountLoaded, allAddresses } = useContext(AccountContext);
  const { orderHistory } = useCart();

  const [userName, setUserName] = useState('');

  const defaultAddress = useMemo(
    () => allAddresses.find((address) => address.isDefaultShippingAddress || address.isDefaultBillingAddress),
    [allAddresses],
  );

  useEffect(() => {
    if (defaultAddress) {
      setUserName(`${defaultAddress.firstName} ${defaultAddress.lastName}`);
      return;
    }

    if (!accountLoaded) {
      return;
    }
    
    if (!account?.accountId) {
      setUserName('');
      return;
    }

    let isCancelled = false;

    const getUserNameFromOrderHistory = async () => {
      const orders = await orderHistory?.(1);

      if (isCancelled) return;

      const latestOrder = orders[0];

      const address = latestOrder?.shippingAddress || latestOrder?.billingAddress;

      if (address?.firstName || address?.lastName) {
        setUserName(`${address.firstName ?? ''} ${address.lastName ?? ''}`.trim());
      } else {
        setUserName('');
      }
    };

    void getUserNameFromOrderHistory();

    return () => {
      isCancelled = true;
    };
  }, [accountLoaded, account?.accountId, defaultAddress, orderHistory]);

  return userName;
};

export const getHeadingStyles = (heading: any, isDesktopSize: boolean) => {
  if (!heading?.[0]) return {};
  return {
    fontSize: isDesktopSize ? `${heading[0].desktopHeadingFontSize}px` : `${heading[0].mobileHeadingFontSize}px`,
    color: isDesktopSize ? heading[0].desktopHeadingFontColor : heading[0].mobileHeadingFontColor,
    fontWeight: isDesktopSize
      ? fontWeightMap[heading[0]?.desktopHeadingFontWeight]
      : fontWeightMap[heading[0]?.mobileHeadingFontWeight],
  };
};

/**
 * Custom hook to determine whether the current tastic (component) should be displayed based on the URL.
 * It checks if the URL contains '/account' and if the 'hash' query parameter matches the ACCOUNT_DASHBOARD tab.
 * If '/account' is not present in the URL, it returns true to show the tastic by default.
 *
 * @returns {boolean} True if the tastic should be shown, false otherwise.
 */

export const useCheckPathAndHash = (path: string, hashKey: string): boolean => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return useMemo(() => {
    // If this is not the target path, show the component
    if (!pathname.includes(path)) {
      return true;
    }

    const hashParam = searchParams.get('hash');

    return hashParam === hashKey;
  }, [pathname, searchParams, path, hashKey]);
};

/**
 * This will provide the concatenated list of discounts applied on the order.
 * @param orderDetails The order details object containing discount information.
 * @param enableCartDiscountChanges A boolean indicating whether cart discount changes are enabled.
 * @returns An array of discount objects applicable to the order.
 */
export const getDiscountList = (orderDetails: any, enableCartDiscountChanges: boolean) => {
  const discountCodes =
    orderDetails?.discountCodes?.map((discount: any) => ({
      couponCode: discount?.code || discount?.name,
      discountAmount: discount?.value,
      discountId: discount?.discountCodeId,
      cartDiscountIds: discount?.discounts?.map((cartDiscount: any) => cartDiscount?.cartDiscountId) || [],
    })) || [];

  const discounts = enableCartDiscountChanges ? orderDetails?.allDiscounts?.couponDiscounts || [] : discountCodes || [];

  const allDiscounts = discounts.concat(
    discountCodes.filter(
      (discountCode: any) =>
        !discounts.some((discount: any) => discountCode?.cartDiscountIds?.includes(discount?.discountId)),
    ),
  );

  return allDiscounts;
};
